import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CodingProblemBuilder, { defaultCodingData } from './CodingProblemBuilder';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname.includes('ondigitalocean.app') ||
      window.location.hostname.includes('services.knls.in'))
  ) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const parseProblemData = (row) => {
  const raw = row?.problem_data || row?.problemData || {};
  return {
    ...defaultCodingData,
    ...raw,
    starterCode: {
      ...defaultCodingData.starterCode,
      ...(raw.starterCode || {}),
    },
    sampleTestCases:
      Array.isArray(raw.sampleTestCases) && raw.sampleTestCases.length
        ? raw.sampleTestCases
        : defaultCodingData.sampleTestCases,
    hiddenTestCases:
      Array.isArray(raw.hiddenTestCases) && raw.hiddenTestCases.length
        ? raw.hiddenTestCases
        : defaultCodingData.hiddenTestCases,
  };
};

const emptyForm = () => ({
  title: '',
  description: '',
  tags: '',
  codingData: { ...defaultCodingData },
});

export default function CodingProblemsManager({ onSelectProblem, selectMode = false }) {
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const loadProblems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/lms/coding/library`, {
        headers: getAuthHeaders(),
      });
      setProblems(response.data?.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load coding problems');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProblems();
  }, [loadProblems]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return problems;
    return problems.filter((row) => {
      const haystack = [
        row.title,
        row.description,
        ...(Array.isArray(row.tags) ? row.tags : []),
        parseProblemData(row).instructions,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [problems, search]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      title: row.title || '',
      description: row.description || '',
      tags: Array.isArray(row.tags) ? row.tags.join(', ') : '',
      codingData: parseProblemData(row),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        tags: form.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        problemData: form.codingData,
      };
      if (editingId) {
        await axios.put(`${API_BASE_URL}/lms/coding/library/${editingId}`, payload, {
          headers: getAuthHeaders(),
        });
        setSuccess('Problem updated.');
      } else {
        await axios.post(`${API_BASE_URL}/lms/coding/library`, payload, {
          headers: getAuthHeaders(),
        });
        setSuccess('Problem saved to library.');
      }
      setDialogOpen(false);
      await loadProblems();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save problem');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete "${row.title}" from library?`)) return;
    setError('');
    try {
      await axios.delete(`${API_BASE_URL}/lms/coding/library/${row.id}`, {
        headers: getAuthHeaders(),
      });
      setSuccess('Problem deleted.');
      await loadProblems();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to delete problem');
    }
  };

  const handleUse = (row) => {
    if (!onSelectProblem) return;
    onSelectProblem({
      title: row.title,
      description: row.description,
      codingData: parseProblemData(row),
      libraryId: row.id,
    });
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: 240, display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {!selectMode && (
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight={800}>
            Coding Problems Library
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create reusable coding problems here, then attach them to any course chapter from the LMS Builder.
          </Typography>
        </Stack>
      )}

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {success ? <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert> : null}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search problems..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
        />
        {!selectMode ? (
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
            New problem
          </Button>
        ) : null}
      </Stack>

      {filtered.length === 0 ? (
        <Alert severity="info">
          No coding problems in the library yet. Create one to reuse across courses.
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          {filtered.map((row) => {
            const data = parseProblemData(row);
            return (
              <Card key={row.id} variant="outlined">
                <CardContent>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    <Box>
                      <Typography fontWeight={700}>{row.title}</Typography>
                      {row.description ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {row.description}
                        </Typography>
                      ) : null}
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={`${data.totalMarks} marks`} />
                        <Chip
                          size="small"
                          label={data.autoGradingEnabled ? 'Auto-grade' : 'Manual'}
                          color={data.autoGradingEnabled ? 'success' : 'warning'}
                        />
                        {(data.languages || []).map((lang) => (
                          <Chip key={lang} size="small" variant="outlined" label={lang.toUpperCase()} />
                        ))}
                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      {selectMode ? (
                        <Button size="small" variant="contained" onClick={() => handleUse(row)}>
                          Use this
                        </Button>
                      ) : (
                        <>
                          <IconButton size="small" onClick={() => openEdit(row)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>{editingId ? 'Edit library problem' : 'New library problem'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Library title"
              fullWidth
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
            />
            <TextField
              label="Short description (optional)"
              fullWidth
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            />
            <TextField
              label="Tags (comma separated)"
              fullWidth
              value={form.tags}
              onChange={(e) => setForm((prev) => ({ ...prev, tags: e.target.value }))}
            />
            <CodingProblemBuilder
              value={form.codingData}
              onChange={(codingData) => setForm((prev) => ({ ...prev, codingData }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save to library'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
